// src/pages/Members.jsx
import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const getRoleBadge = (member) => {
  if (member?.isAdmin)
    return { label: 'Admin', cls: 'bg-purple-500 text-white' };
  if (member?.role === 'board' || member?.role === 'Board')
    return { label: 'Board', cls: 'bg-blue-500 text-white' };
  return { label: 'Member', cls: 'bg-orange text-white' };
};

const useDebounced = (value, delay = 200) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

/** Stable alphabetical sort — null-safe. */
const compareByName = (a, b) => {
  const an = (a.name || a.displayName || a.email || '').toLowerCase();
  const bn = (b.name || b.displayName || b.email || '').toLowerCase();
  if (an < bn) return -1;
  if (an > bn) return 1;
  return 0;
};

/* ================================================================== */
/* Toast                                                               */
/* ================================================================== */

const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  const config = {
    success: { bg: 'bg-green-500', icon: 'fa-circle-check' },
    error: { bg: 'bg-red-500', icon: 'fa-circle-exclamation' },
    warning: { bg: 'bg-yellow-500', icon: 'fa-triangle-exclamation' },
  }[type] || { bg: 'bg-green-500', icon: 'fa-circle-check' };

  return (
    <motion.div
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 340, damping: 26 }}
      className={`fixed bottom-4 right-4 z-[80] px-4 py-3 rounded-lg shadow-lg text-white flex items-center gap-2 ${config.bg}`}
    >
      <i className={`fas ${config.icon}`} aria-hidden="true" />
      <span className="text-sm font-medium">{message}</span>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="ml-1 text-white/80 hover:text-white"
      >
        <i className="fas fa-times text-xs" aria-hidden="true" />
      </button>
    </motion.div>
  );
};

/* ================================================================== */
/* Member Card Skeleton                                                */
/* ================================================================== */

const MemberCardSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-5 text-center animate-pulse">
    <div className="w-24 h-24 mx-auto mb-3 rounded-full bg-gray-200 dark:bg-gray-700" />
    <div className="h-4 w-24 mx-auto bg-gray-200 dark:bg-gray-700 rounded mb-2" />
    <div className="h-3 w-32 mx-auto bg-gray-200 dark:bg-gray-700 rounded mb-1" />
    <div className="h-3 w-20 mx-auto bg-gray-200 dark:bg-gray-700 rounded mb-3" />
    <div className="h-5 w-16 mx-auto bg-gray-200 dark:bg-gray-700 rounded-full" />
  </div>
);

/* ================================================================== */
/* Member Card                                                         */
/* ================================================================== */

const MemberCard = ({
  member,
  currentUser,
  isOnline,
  onMessage,
  isStartingChat,
}) => {
  const badge = getRoleBadge(member);
  const isSelf = currentUser?.uid === member.id;
  const displayName =
    member.name || member.displayName || member.email?.split('@')[0] || 'User';
  const initial = displayName.charAt(0).toUpperCase();

  const allSkills = useMemo(
    () =>
      member.skills
        ? member.skills
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    [member.skills],
  );

  const skills = allSkills.slice(0, 3);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      whileHover={{ y: -4 }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all overflow-hidden group relative"
    >
      <Link
        to={`/profile/${member.id}`}
        className="block p-5 text-center focus:outline-none focus:ring-2 focus:ring-orange focus:ring-inset rounded-2xl"
        aria-label={`View ${displayName}'s profile`}
      >
        {/* Avatar with online indicator */}
        <div className="relative w-24 h-24 mx-auto mb-3">
          <div className="w-full h-full rounded-full bg-gradient-to-br from-orange to-orange-dark flex items-center justify-center text-white text-3xl font-bold shadow-md overflow-hidden ring-2 ring-white dark:ring-gray-700">
            {member.photoUrl ? (
              <img
                src={member.photoUrl}
                alt={displayName}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            ) : (
              <span>{initial}</span>
            )}
          </div>
          {isOnline && !isSelf && (
            <span
              className="absolute bottom-1 right-1 w-5 h-5 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"
              title="Online"
              aria-label="Online"
            />
          )}
        </div>

        {/* Name */}
        <h3 className="text-base font-bold text-gray-800 dark:text-white truncate">
          {displayName}
          {isSelf && (
            <span className="text-orange text-xs ml-1.5 font-normal">
              (You)
            </span>
          )}
        </h3>

        {/* Position */}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
          {member.position || 'Professional Member'}
        </p>

        {/* Company */}
        {member.company && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
            <i className="fas fa-building mr-1" aria-hidden="true" />
            {member.company}
          </p>
        )}

        {/* Role badge */}
        <span
          className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide mt-2 ${badge.cls}`}
        >
          {badge.label}
        </span>

        {/* Skill chips */}
        {skills.length > 0 && (
          <div className="flex flex-wrap justify-center gap-1 mt-3">
            {skills.map((s, i) => (
              <span
                key={i}
                className="bg-orange/10 text-orange text-[10px] px-2 py-0.5 rounded-full"
              >
                {s}
              </span>
            ))}
            {allSkills.length > 3 && (
              <span className="text-gray-400 text-[10px] self-center">
                +{allSkills.length - 3}
              </span>
            )}
          </div>
        )}
      </Link>

      {/* Message button (hover on desktop, always on mobile) */}
      {currentUser && !isSelf && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onMessage(member);
          }}
          disabled={isStartingChat}
          aria-label={`Message ${displayName}`}
          title="Send message"
          className="absolute bottom-3 right-3 bg-orange text-white p-2 rounded-full shadow-md hover:bg-orange-dark active:scale-95 transition-all md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i
            className={`fas ${
              isStartingChat ? 'fa-spinner fa-spin' : 'fa-comment-dots'
            } text-sm`}
            aria-hidden="true"
          />
        </button>
      )}
    </motion.div>
  );
};

/* ================================================================== */
/* Members Page                                                        */
/* ================================================================== */

const Members = () => {
  const { user } = useAuth();
  const { createPrivateChat, getUserOnlineStatus } = useChat();
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [startingChat, setStartingChat] = useState(null);
  const [toast, setToast] = useState(null);

  const debouncedSearch = useDebounced(searchTerm, 150);
  const searchInputRef = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  /* ---------------- Live members listener ---------------- */
  useEffect(() => {
    setLoading(true);

    const q = query(collection(db, 'users'), orderBy('name', 'asc'));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const usersData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Exclude blocked / rejected users
        const active = usersData
          .filter(
            (u) =>
              u.isBlocked !== true && u.registrationStatus !== 'rejected',
          )
          .sort(compareByName);

        setMembers(active);
        setLoading(false);
      },
      (err) => {
        console.error('Members listener failed:', err);
        showToast('Failed to load members', 'error');
        setLoading(false);
      },
    );

    return () => unsub();
  }, [showToast]);

  /* ---------------- Filtering ---------------- */
  /**
   * NOTE: `getUserOnlineStatus` is read here for the 'online' filter.
   * For this filter to update reactively when presence changes, the
   * `ChatContext` provider must re-render (or provide a stable but
   * reactive accessor). If online status ever appears stale, that is
   * the layer to fix — not this page.
   */
  const filtered = useMemo(() => {
    let list = members;

    if (filter === 'admin') list = list.filter((m) => m.isAdmin);
    else if (filter === 'board')
      list = list.filter((m) => m.role === 'board' || m.role === 'Board');
    else if (filter === 'members')
      list = list.filter(
        (m) => !m.isAdmin && m.role !== 'board' && m.role !== 'Board',
      );
    else if (filter === 'online')
      list = list.filter((m) => getUserOnlineStatus?.(m.id));

    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((m) => {
        const haystack = [
          m.name,
          m.displayName,
          m.email,
          m.company,
          m.position,
          m.skills,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    return list;
  }, [members, filter, debouncedSearch, getUserOnlineStatus]);

  /* ---------------- Stats ---------------- */
  const stats = useMemo(() => {
    const total = members.length;
    const online = members.filter((m) => getUserOnlineStatus?.(m.id)).length;
    const admins = members.filter((m) => m.isAdmin).length;
    const board = members.filter(
      (m) => m.role === 'board' || m.role === 'Board',
    ).length;
    return { total, online, admins, board };
  }, [members, getUserOnlineStatus]);

  /* ---------------- Message handler ---------------- */
  const handleMessage = useCallback(
    async (member) => {
      if (!user || member.id === user.uid) {
        showToast("You can't message yourself", 'warning');
        return;
      }

      setStartingChat(member.id);
      try {
        const chat = await createPrivateChat(member.id);
        if (chat) {
          navigate('/chat');
        }
      } catch (err) {
        if (err.message === 'ENCRYPTION_NOT_READY') {
          showToast(
            `${
              member.name || member.email || 'This user'
            } hasn't set up encryption yet.`,
            'warning',
          );
        } else {
          console.error('Failed to start chat:', err);
          showToast('Could not start chat. Please try again.', 'error');
        }
      } finally {
        // Only clear if this is still the active request — prevents a
        // fast follow-up click on another member from being wiped out
        // by the previous request's cleanup.
        setStartingChat((prev) => (prev === member.id ? null : prev));
      }
    },
    [user, createPrivateChat, navigate, showToast],
  );

  /* ---------------- Filter tabs ---------------- */
  const tabs = [
    { id: 'all', label: 'All', count: stats.total },
    { id: 'online', label: 'Online', count: stats.online, live: true },
    { id: 'admin', label: 'Admins', count: stats.admins },
    { id: 'board', label: 'Board', count: stats.board },
    {
      id: 'members',
      label: 'Members',
      count: stats.total - stats.admins - stats.board,
    },
  ];

  /* ---------------- Loading ---------------- */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-6 px-3 md:px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-6">
            <div className="h-8 w-56 mx-auto bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-4 mb-4">
            <div className="h-12 w-full bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <MemberCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ---------------- Render ---------------- */
  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-6 px-3 md:px-4">
      <AnimatePresence>
        {toast && (
          <Toast
            key="toast"
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>

      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white relative inline-block">
            Members Directory
            <span className="absolute -bottom-2 left-0 w-1/2 h-1 bg-orange rounded-full" />
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-3 text-sm">
            Connect with{' '}
            <span className="font-semibold text-orange">{stats.total}</span>{' '}
            IIEE-ERCSA member{stats.total === 1 ? '' : 's'}
          </p>
        </div>

        {/* Search + filters (sticky on scroll) */}
        <div className="sticky top-16 z-30 bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-4 mb-4 border border-gray-100 dark:border-gray-700/60 backdrop-blur-sm">
          {/* Search input */}
          <div className="relative mb-4">
            <i
              className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name, company, position, or skills..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search members"
              className="w-full pl-11 pr-11 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  searchInputRef.current?.focus();
                }}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-orange w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                <i className="fas fa-times-circle" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* Filter tabs */}
          <div className="flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setFilter(t.id)}
                aria-pressed={filter === t.id}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1.5 ${
                  filter === t.id
                    ? 'bg-orange text-white shadow-sm'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {t.live && (
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      filter === t.id ? 'bg-white' : 'bg-green-500 animate-pulse'
                    }`}
                  />
                )}
                {t.label}
                {t.count > 0 && (
                  <span
                    className={`text-[10px] px-1.5 rounded-full ${
                      filter === t.id
                        ? 'bg-white/20'
                        : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                  >
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Empty state */}
        {filtered.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16 bg-white dark:bg-gray-800 rounded-2xl shadow-sm"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <i
                className="fas fa-user-slash text-2xl text-gray-400"
                aria-hidden="true"
              />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">
              {debouncedSearch ? 'No matches found' : 'No members to show'}
            </h3>
            <p className="text-sm text-gray-500 max-w-xs mx-auto">
              {debouncedSearch
                ? `No members match "${debouncedSearch}". Try a different search.`
                : filter === 'online'
                ? 'No members are online right now. Check back later!'
                : filter !== 'all'
                ? 'No members in this category.'
                : 'Members will appear here once they register.'}
            </p>
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="mt-4 text-orange hover:underline text-sm font-medium"
              >
                Clear search
              </button>
            )}
            {!searchTerm && filter !== 'all' && (
              <button
                type="button"
                onClick={() => setFilter('all')}
                className="mt-4 text-orange hover:underline text-sm font-medium"
              >
                View all members
              </button>
            )}
          </motion.div>
        )}

        {/* Grid */}
        {filtered.length > 0 && (
          <motion.div
            layout
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
          >
            <AnimatePresence mode="popLayout">
              {filtered.map((member) => (
                <MemberCard
                  key={member.id}
                  member={member}
                  currentUser={user}
                  isOnline={getUserOnlineStatus?.(member.id) || false}
                  onMessage={handleMessage}
                  isStartingChat={startingChat === member.id}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Showing count */}
        {filtered.length > 0 && (
          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-6">
            Showing{' '}
            <span className="font-semibold">{filtered.length}</span> of{' '}
            <span className="font-semibold">{members.length}</span> members
          </p>
        )}
      </div>
    </div>
  );
};

export default Members;